import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addEffect,
  flowMax,
} from 'ad-hok'
import { Trans } from 'react-i18next'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { trackEvent } from 'shared/src/utils/analytics'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import backgroundImg from 'assets/background-banner.png'
import appIcon from 'assets/rodeo-app-icon.svg'
import closeIcon from 'assets/close-icon.svg'
import appleLogo from 'assets/apple-logo.svg'
import { sansSerifFontFamily, serifFontFamily } from '../../utils/styles'
import { NOTIFICATION_Z_INDEX } from '../../utils/zIndex'
import { Button } from '../Button'
import { Banner as BannerInterface } from './index'

const useStyles = createUseStyles({
  backgroundHider: {
    zIndex: NOTIFICATION_Z_INDEX,
    position: 'fixed',
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(1px)',
  },
  container: {
    position: 'fixed',
    minWidth: 320,
    maxWidth: '428px',
    width: '100vw',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgb(60 60 60);',
    bottom: 0,
    letterSpacing: '0.2px',
  },
  containerBg: {
    backgroundImage: `url(${backgroundImg})`,
    backgroundSize: 'cover',
    backgroundBlendMode: 'lighten',
    backgroundColor: 'rgb(1 1 1 / 68%)',
    paddingLeft: 28,
    paddingRight: 28,
    paddingTop: 24,
    paddingBottom: 24,
  },
  icon: {
    width: '116px',
  },
  contentHolder: {
    width: '100%',
    marginTop: '43px',
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
  },
  bottomTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '28px',
    fontFamily: sansSerifFontFamily,
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 1)',
    letterSpacing: 0.16,
    maxWidth: 250,
  },
  textStyle: {
    fontFamily: serifFontFamily,
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '44.8px',
    letterSpacing: '0.4px',
  },
  actionBottom: {
    height: 72,
    width: 'calc(100% - 16px)',
    marginLeft: '8px',
    marginTop: 41,
    paddingLeft: 21,
    paddingRight: 21,
    backgroundColor: '#ffffff',
    borderRadius: 18,
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    color: '#363432',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  appleLogo: {
    marginLeft: '-25px',
    marginRight: '25px',
  },
  closeBtn: {
    width: 36,
    height: 36,
    top: '12px',
    right: '12px',
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtnImg: {
    width: '32px',
  },
  italic: {
    fontStyle: 'italic',
  },
})

interface Props {
  banner: BannerInterface
  onAction: () => void
}

export const Banner: FC<Props> = flowMax(
  addState('touchStart', 'setTouchStart', typedAs<number | null>(null)),
  addState('touchEnd', 'setTouchEnd', typedAs<number | null>(null)),
  addDisplayName('Banner'),
  addNavigate,
  addTranslationHelpers,
  addEffect(() => () => {
    document.documentElement.style.setProperty('--allow-pull-refresh', 'none')
  }),
  addHandlers({
    onTouchStart:
      ({ setTouchEnd, setTouchStart }) =>
      (e: any) => {
        setTouchEnd(null)
        setTouchStart(e.targetTouches[0].clientY)
      },
    onTouchMove:
      ({ setTouchEnd }) =>
      (e: any) => {
        setTouchEnd(e.targetTouches[0].clientY)
      },
    onTouchEnd:
      ({ touchStart, touchEnd, onAction }) =>
      () => {
        const minSwipeDistance = 50
        if (!touchStart || !touchEnd) return
        const distance = touchStart - touchEnd
        const isBottomtSwipe = distance < -minSwipeDistance
        if (isBottomtSwipe) {
          document.documentElement.style.setProperty(
            '--allow-pull-refresh',
            'auto'
          )
          onAction()
        }
      },
    onClick:
      ({ banner: { button }, navigate, onAction }) =>
      () => {
        if (button) {
          button.onClick?.()

          if (button.onClickAnalyticsEvent) {
            trackEvent(
              button.onClickAnalyticsEvent.options,
              button.onClickAnalyticsEvent.params
            )
          }

          if (button.external) {
            window.open(button.navigateTo, '_blank')
          } else {
            navigate(button.navigateTo, { state: button.state })
          }
        }
        document.documentElement.style.setProperty(
          '--allow-pull-refresh',
          'auto'
        )
        onAction()
      },
    onClose:
      ({ banner: { onCloseAnalyticsEvent }, onAction }) =>
      () => {
        if (onCloseAnalyticsEvent) {
          trackEvent(
            onCloseAnalyticsEvent.options,
            onCloseAnalyticsEvent.params
          )
        }
        document.documentElement.style.setProperty(
          '--allow-pull-refresh',
          'auto'
        )
        onAction()
      },
    preventPropagation: () => (e: any) => {
      e.preventPropagation()
    },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    banner: { title, textTranslationKey, button },
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    onClick,
    onClose,
    preventPropagation,
    classes,
    t,
  }) => (
    <div onClick={onClose} className={classes.backgroundHider}>
      <div
        className={cx(classes.container)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={preventPropagation}
      >
        <div className={classes.containerBg}>
          <div className={classes.contentHolder}>
            <img src={appIcon} className={classes.icon} alt="" />
            <div className={classes.bottomTextContainer}>
              <div>{title}</div>
              <div className={classes.textStyle}>
                <Trans i18nKey={textTranslationKey as any} t={t}>
                  <span className={classes.italic} />
                </Trans>
              </div>
            </div>
          </div>
          {button && (
            <Button onClick={onClick} className={classes.actionBottom}>
              <img className={classes.appleLogo} src={appleLogo} alt="" />
              {button.title}
            </Button>
          )}
          {
            <Button onClick={onClose} className={classes.closeBtn}>
              <img src={closeIcon} alt="" className={classes.closeBtnImg} />
            </Button>
          }
        </div>
      </div>
    </div>
  )
)
